import { useEffect, useRef, useState } from "react";

export const Carousel = ({ images = [], alt = "" }) => {
  const trackRef = useRef(null);
  const [index, setIndex] = useState(0);
  const [count, setCount] = useState(images.length);

  useEffect(() => {
    setCount(images.length);
  }, [images]);

  // Update active index based on scroll position
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth;
      const idx = Math.round(el.scrollLeft / w);
      setIndex(idx);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  if (count === 0) return null;

  return (
    <div
      className="relative bg-[#F7E9DD] border-2 border-[#141518] shadow-hard"
      data-testid="gallery-carousel"
    >
      <div
        ref={trackRef}
        className="flex w-full overflow-x-auto snap-x snap-mandatory scroll-smooth aspect-[4/5] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {images.map((src, i) => (
          <div
            key={i}
            className="snap-start shrink-0 w-full h-full"
            data-testid={`gallery-slide-${i}`}
          >
            <img
              src={src}
              alt={`${alt} ${i + 1}`}
              className="block w-full h-full object-cover"
              loading={i === 0 ? "eager" : "lazy"}
              draggable="false"
            />
          </div>
        ))}
      </div>

      {/* Arrows (hidden on mobile, visible on md+) */}
      {count > 1 && (
        <>
          <button
            type="button"
            aria-label="Anterior"
            data-testid="gallery-prev"
            onClick={() => goTo(Math.max(0, index - 1))}
            disabled={index === 0}
            className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white text-[#141518] border-2 border-[#141518] disabled:opacity-30 hover:bg-[#7AD5DC] transition-colors"
          >
            ←
          </button>
          <button
            type="button"
            aria-label="Següent"
            data-testid="gallery-next"
            onClick={() => goTo(Math.min(count - 1, index + 1))}
            disabled={index === count - 1}
            className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center bg-white text-[#141518] border-2 border-[#141518] disabled:opacity-30 hover:bg-[#7AD5DC] transition-colors"
          >
            →
          </button>
        </>
      )}

      {/* Counter + dots */}
      {count > 1 && (
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between pointer-events-none">
          <div className="bg-white text-[#141518] border-2 border-[#141518] px-2.5 py-1 text-[10px] font-condensed font-bold tabular-nums tracking-[0.18em]">
            {String(index + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
          </div>
          <div className="flex items-center gap-1.5 bg-white border-2 border-[#141518] px-2 py-1 pointer-events-auto">
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Anar a la foto ${i + 1}`}
                onClick={() => goTo(i)}
                data-testid={`gallery-dot-${i}`}
                className={`h-2 transition-all ${
                  i === index ? "w-5 bg-[#F0311B]" : "w-2 bg-[#141518]/30"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
